<template>
  <div v-if="visible" class="form-overlay" @click.self="closeForm">
    <div class="records-list">
      <button class="close-btn" @click="closeForm">&times;</button>
      <h2>客流量</h2>
      <div class="chart-container">
        <h3>每日來客數與成交數</h3>
        <Bar
          id="traffic-combined-chart"
          :options="chartOptions"
          :data="combinedChartData"
          :height="200"
        />
      </div>
      <div class="search-wrapper">
        <input type="text" v-model="searchQuery" placeholder="搜尋日期、來源" class="search-input">
      </div>

      <div class="table-wrapper">
        <table class="record-list" v-if="userAuthority <= 1">
          <thead>
            <tr>
              <th>日期</th>
              <th>來源</th>
              <th>來客數</th>
              <th>成交數</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <!-- New Record Input Row -->
            <tr class="new-record-input">
              <td class="date-column">
                <input type="date" v-model="currentRecord.date" data-cy="traffic-date-input">
              </td>
              <td class="name-column">
                <input 
                  id="intro_by"
                  type="text" 
                  v-model="currentRecord.intro_by"
                  list="introByOptions"
                  class="full-width"
                  placeholder="請選擇或輸入來源"
                  data-cy="traffic-source-input"
                >
                <datalist id="introByOptions">
                  <option v-for="introBy in introByOptions" :key="introBy.id" :value="introBy.name">
                    {{ introBy.name }}
                  </option>
                </datalist>
              </td>
              <td class="quantity-column">
                <input 
                  type="number" 
                  v-model.number="currentRecord.count" 
                  min="1"
                  data-cy="traffic-count-input"
                >
              </td>
              <td class="quantity-column">
                <input 
                  type="number" 
                  v-model.number="currentRecord.deal_count" 
                  min="1"
                  data-cy="traffic-deal-input"
                >
              </td>
              <td>
                <button 
                  type="button" 
                  class="add-btn" 
                  @click="addRecord"
                  :disabled="!isValidRecord || userAuthority > 1"
                  data-cy="add-traffic-button"
                >
                  +
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-wrapper" ref="scrollWrapper">
        <table>
          <thead>
            <tr>
              <th>日期</th>
              <th>來客數</th>
              <th>成交數</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="aggregated_record in aggregated_records.slice().reverse()" :key="aggregated_record.id">
              <!-- Order row -->
              <tr 
                @click="toggleExpandedRecordDate(aggregated_record.date)"
                :class="{ 'expanded': expandedRecordDate === aggregated_record.date }"
                class="order-row"
                data-cy="traffic-record-row"
              >
                <td class="date-column" data-cy="traffic-date">{{ aggregated_record.date }}</td>
                <td data-cy="traffic-aggregated-count">{{ aggregated_record.count }}</td>
                <td data-cy="traffic-aggregated-deal">{{ aggregated_record.deal_count }}</td>
              </tr>
              <!-- Items, Disposal and Payment rows -->
              <tr v-if="expandedRecordDate === aggregated_record.date" :key="aggregated_record.date + '-details'" class="details-row">
                <td colspan="11">
                  <div class="details-container">
                    <!-- Add Record Section -->
                    <div class="form-section add-record">
                      <!-- <h3>客流量</h3> -->
                      <div class="record-list-container">
                        <table class="record-list">
                          <thead>
                            <tr>
                              <th>來源</th>
                              <th>來客數</th>
                              <th>成交數</th>
                            </tr>
                          </thead>
                          <tbody>
                            <!-- Existing Traffic Items -->
                            <tr v-for="(item, index) in expandedRecords" :key="index">
                              <td data-cy="traffic-source">{{ item.intro_by }}</td>
                              <td data-cy="traffic-count">{{ item.count }}</td>
                              <td data-cy="traffic-deal">{{ item.deal_count }}</td>
                              <td v-if="userAuthority <= 1">
                                <button 
                                  type="button" 
                                  class="remove-btn" 
                                  @click="removeTrafficRecord(item.id)"
                                  :disabled="userAuthority > 1"
                                >
                                  -
                                </button>
                              </td>
                            </tr>
                          </tbody>
                          <tfoot v-if="userAuthority <= 1">
                            <tr><td colspan="1" style="padding:10px"></td></tr>
                            <tr class="total-row">
                              <td colspan="1" class="total-count-label">小計</td>
                              <td class="total-count-value">
                                {{ expandedRecords.reduce((sum, item) => sum + (item.count || 0), 0) }}
                              </td>
                              <td class="total-deal-count-value">
                                {{ expandedRecords.reduce((sum, item) => sum + (item.deal_count || 0), 0) }}
                              </td>
                            </tr>

                            <!-- New Record Input Row -->
                            <tr class="new-record-input">
                              <td class="name-column">
                                <input 
                                  id="intro_by"
                                  type="text" 
                                  v-model="currentRecord.intro_by"
                                  list="introByOptions"
                                  class="full-width"
                                  placeholder="請選擇或輸入來源"
                                  data-cy="traffic-source-input"
                                >
                                <datalist id="introByOptions">
                                  <option v-for="introBy in introByOptions" :key="introBy.id" :value="introBy.name">
                                    {{ introBy.name }}
                                  </option>
                                </datalist>
                              </td>
                              <td class="quantity-column">
                                <input 
                                  type="number" 
                                  v-model.number="currentRecord.count" 
                                  min="1"
                                  data-cy="traffic-count-input"
                                >
                              </td>
                              <td class="quantity-column">
                                <input 
                                  type="number" 
                                  v-model.number="currentRecord.deal_count" 
                                  min="1"
                                  data-cy="traffic-deal-input"
                                >
                              </td>
                              <td>
                                <button 
                                  type="button" 
                                  class="add-btn" 
                                  @click="addRecord"
                                  :disabled="!isValidRecord || userAuthority > 1"
                                  data-cy="add-traffic-button"
                                >
                                  +
                                </button>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                    <div v-if="!expandedRecords" class="loading-details">
                      Loading traffic details...
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  components: {
    Bar
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    userAuthority: {
      type: Number,
      default: 2,
    },
  },
  emits: ['close'],
  data() {
    return {
      aggregated_records: [],  // Initialize as empty array
      expandedRecordDate: null,
      expandedRecords: [],
      currentRecord: {
        date: new Date().toISOString().split('T')[0],
        intro_by: '',
        count: 0,
        deal_count: 0,
      },
      introByOptions: [],

      showForm: false,
      isLoading: false,
      hasMore: true,
      page: 1,
      searchQuery: '',
      debouncedSearch: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          }
        },
        animation: false
      }
    };
  },
  watch: {
    searchQuery() {
      this.debouncedSearch();
    },
    "currentRecord.deal_count": {
      handler(newVal) {
        if (this.currentRecord.count < newVal) {
          this.currentRecord.count = newVal;
        }
      }
    },
    visible(newVal) {
      if (newVal) {
        // Wait for the next tick to ensure the DOM is updated
        this.$nextTick(() => {
          const tableWrapper = this.$refs.scrollWrapper;
          if (tableWrapper) {
            tableWrapper.addEventListener('scroll', this.handleScroll);
          }
        });

        // Initial load
        this.fetchTraffic(true);
        this.fetchIntroByOptions();
      } else {
        const tableWrapper = this.$refs.scrollWrapper;
        if (tableWrapper) {
          tableWrapper.removeEventListener('scroll', this.handleScroll);
        }
      }
    }
  },
  computed: {
    isValidRecord() {
      return this.currentRecord.date && this.currentRecord.count > 0 && this.currentRecord.deal_count >= 0;
    },
    combinedChartData() {
      const labels = this.aggregated_records.map(record => {
        const date = new Date(record.date);
        const monthDay = date.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
        const weekday = date.toLocaleDateString('zh-TW', { weekday: 'short' }).replace("週", "");
        return `${monthDay}${weekday}`;
      });
      const customerData = this.aggregated_records.map(record => record.count);
      const dealData = this.aggregated_records.map(record => record.deal_count);
      return {
        labels: labels,
        datasets: [
          {
            label: '來客數',
            backgroundColor: '#f7d06b',
            data: customerData
          },
          {
            label: '成交數',
            backgroundColor: '#66BB6A',
            data: dealData
          }
        ]
      };
    }
  },
  created() {
    this.debouncedSearch = this.debounce(() => {
      this.fetchTraffic(true);
    }, 500);
  },
  methods: {
    closeForm() {
      this.$emit('close');
      // Reset the expanded state when closing
      this.expandedRecordDate = null;
      this.expandedRecords = [];
    },
    async fetchTraffic(init = false) {
      if (!init && (this.isLoading || !this.hasMore)) return;
      this.isLoading = true;
      this.page = init ? 1 : this.page;

      try {
        let url = `/api/traffic?page=${this.page}&per_page=30`;
        if (this.searchQuery) {
          url += `&search=${this.searchQuery}`;
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        if (init) {
          this.aggregated_records = data.aggregated_records;
        } else {
          this.aggregated_records = [...this.aggregated_records, ...data.aggregated_records];
        }
        // Sort aggregated_records by date in ascending order
        this.aggregated_records.sort((a, b) => new Date(a.date) - new Date(b.date));
        this.hasMore = data.has_next;
        if (this.hasMore) {
          this.page++;
        }
      } catch (error) {
        console.error('Error fetching traffic:', error);
      } finally {
        this.isLoading = false;
      }
    },
    toggleExpandedRecordDate(recordDate) {
      if (this.expandedRecordDate === recordDate) {
        this.expandedRecordDate = null;
      } else {
        this.expandedRecordDate = recordDate;
        this.fetchRecordsByDate(recordDate);
        this.currentRecord.date = recordDate;
      }
    },
    async fetchIntroByOptions() {
      try {
        const response = await fetch('/api/customer_sources');
        if (!response.ok) throw new Error('Network response was not ok');
        
        this.introByOptions = await response.json();
      } catch (error) {
        console.error('Error fetching intro by options:', error);
      }
    },
    async fetchRecordsByDate(recordDate) {
      try {
        let url = `/api/traffic_by_date/${recordDate}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        this.expandedRecords = data;
      } catch (error) {
        console.error('Error fetching traffic records:', error);
      }
    },
    async addRecord() {
      try {
        const response = await fetch('/api/traffic', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(this.currentRecord)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create traffic record');
        }
        
        const add_date = this.currentRecord.date;
        // Reset the form
        this.currentRecord = {
          date: add_date,
          intro_by: '',
          count: 0,
          deal_count: 0,
        };

        // Refresh the records for the current date
        if (add_date) {
          await this.fetchRecordsByDate(add_date);
        }
        
        // Check if the date exists in aggregated_records
        const aggregated_record = this.aggregated_records.find(record => record.date === add_date)
        if (aggregated_record) {
          this.updateAggregatedRecord(add_date);
        } else {
          await this.fetchTraffic(true);
        }
      } catch (error) {
        console.error('Error creating traffic record:', error);
        alert(error.message || 'Failed to create traffic record');
      }
    },
    async removeTrafficRecord(recordId) {
      try {
        const response = await fetch(`/api/traffic/${recordId}`, {
          method: 'DELETE',
        });
        if (!response.ok) throw new Error('Network response was not ok');
        
        // Remove the record from the local list instead of a full refetch
        this.expandedRecords = this.expandedRecords.filter(r => r.id !== recordId);
        this.updateAggregatedRecord(this.expandedRecordDate);
      } catch (error) {
        console.error('Error deleting traffic record:', error);
        alert('刪除客流量時發生錯誤');
      }
    },
    updateAggregatedRecord(date) {
      const aggregated_record = this.aggregated_records.find(record => record.date === date)
      if (aggregated_record) {
        aggregated_record.count = this.expandedRecords.reduce((sum, item) => sum + (item.count || 0), 0);
        aggregated_record.deal_count = this.expandedRecords.reduce((sum, item) => sum + (item.deal_count || 0), 0); 
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    debounce(func, delay) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
      };
    },
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollHeight - scrollTop <= clientHeight + 100 && !this.isLoading && this.hasMore) {
        this.fetchTraffic();
      }
    }
  }
}
</script>

<style scoped>
.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
  z-index: 1000;
  overflow-y: auto;
}

.records-list {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  margin: 20px 0;
}

.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start; /* Changed to flex-start to ensure content starts from the top */
  z-index: 1000;
}

.delivery-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Checkbox styling */
.delivery-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3b82f6;
  border-radius: 0.25rem;
  border: 1px solid #cbd5e1;
}

.delivery-checkbox:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.delivery-checkbox:focus {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}
.sections-container {
  display: flex;
  flex-wrap: wrap;
}

.sections-container > .section {
  flex: 1;
  min-width: 300px;
  border-radius: 8px;
}

.basic-info {
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.info-item {
  display: flex;
  line-height: 1.6;
}

.info-label {
  font-weight: 600;
  color: #495057;
  min-width: 80px;
  margin-right: 0.5rem;
}

.info-value {
  color: #212529;
  word-break: break-word;
}

.action-buttons {
  grid-column: 1 / -1;
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}

.edit-btn, .delete-btn {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.edit-btn {
  background-color: #4CAF50;
  color: white;
}

.edit-btn:hover {
  background-color: #45a049;
}

.delete-btn {
  background-color: #f44336;
  color: white;
}

.delete-btn:hover {
  background-color: #d32f2f;
}

.edit-btn:active {
  background-color: #3d8b40;
}

.total-count-label {
  background-color: #f8f9fa;
}

.orders-page {
  position: relative;
  min-height: 100vh;
}

.records-list {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  margin-bottom: 60px; /* Add space for footer */
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: auto;
  max-height: calc(100vh - 200px);
  position: relative;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
}

.order-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.order-row:hover {
  background-color: #f5f5f5;
}

.order-row.expanded {
  background-color: #e3f2fd;
}

.details-row {
  background-color: #fafafa;
}

.details-container {
  padding: 20px;
}

.section {
  margin-bottom: 30px;
}

.section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.1em;
}

.details-table {
  width: 100%;
  margin-top: 10px;
  background-color: white;
  border: 1px solid #eee;
}

.details-table th {
  background-color: #f0f0f0;
  font-size: 0.9em;
  padding: 10px;
  border-bottom: 2px solid #ddd;
}

.details-table td {
  padding: 8px 10px;
  font-size: 0.9em;
  border-bottom: 1px solid #eee;
}

.detail-row:hover {
  background-color: #f8f9fa;
}

.loading-details {
  text-align: center;
  padding: 20px;
  color: #666;
}

.ship-details {
  font-size: 0.85em;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.ship-details div {
  margin: 4px 0;
}

.ship-details strong {
  color: #2c3e50;
}

.no-ship {
  color: #666;
  font-style: italic;
  font-size: 0.85em;
}

@media (max-width: 768px) {
  .records-list {
    padding: 10px;
  }
  
  th, td {
    padding: 8px;
  }

  .section h3 {
    font-size: 1em;
  }
  
  .details-table th,
  .details-table td {
    padding: 6px 8px;
    font-size: 0.85em;
  }

  .ship-details {
    font-size: 0.8em;
    padding: 4px;
  }
  
  .ship-details div {
    margin: 2px 0;
  }
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  background-color: white;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  text-align: center;
  z-index: 100;
}

.new-order-btn {
  background-color: #2c3e50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.new-order-btn:hover {
  background-color: #1a252f;
}

.search-wrapper {
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
}


.add-btn {
  background-color: #4CAF50;
  color: white;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.add-btn:hover {
  background-color: #45a049;
}

.add-btn:disabled,
.remove-btn:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.remove-btn {
  background-color: #f44336;
  color: white;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #d32f2f;
}

.chart-container {
  margin-bottom: 50px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  height: 300px; /* Explicitly set height for the container */
}

.chart-container h3 {
  text-align: center;
  margin-bottom: 15px;
  color: #333;
}
</style>
